import type { QueryFilters } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { organizationTaskContract } from "@common/contracts/organization-task.contract";
import type { TaskListQuery } from "@common/contracts/task.contract";
import { TaskSchema, type Task } from "@common/types/task";
import type { TaskThreadUnreadCount } from "@common/schemas/task.schema";
import type {
    ClientTaskPreview,
    ClientTaskPreviewsResponse,
} from "@common/schemas/organization-task.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const organizationTaskClient = getBackendAPIClient(organizationTaskContract);

export const organizationTaskQueryKeys = {
    all: () => ["organization-tasks"] as const,
    todoCount: (organizationId: string) =>
        [
            ...organizationTaskQueryKeys.all(),
            organizationId,
            "todo-count",
        ] as const,
    lists: () => [...organizationTaskQueryKeys.all(), "tasks"] as const,
    tasks: (organizationId: string, query?: TaskListQuery) =>
        [...organizationTaskQueryKeys.lists(), organizationId, query] as const,
    threadUnreadCounts: (organizationId: string, query?: TaskListQuery) =>
        [
            ...organizationTaskQueryKeys.all(),
            organizationId,
            "thread-unread-counts",
            query,
        ] as const,
    clientTaskPreviews: ({
        organizationId,
        workspaceIds,
    }: {
        organizationId: string;
        workspaceIds: readonly string[];
    }) =>
        [
            ...organizationTaskQueryKeys.all(),
            organizationId,
            "client-task-previews",
            workspaceIds,
        ] as const,
};

/** Every organization task list in the cache, whatever its organization or query. */
export const organizationTaskListsFilter: QueryFilters = {
    queryKey: organizationTaskQueryKeys.lists(),
};

interface FetchOrganizationTasksParams {
    organizationId: string;
    query: TaskListQuery;
}

/** Tasks matching `query` across the organization's clients, in one request. */
export async function fetchOrganizationTasks({
    organizationId,
    query,
}: FetchOrganizationTasksParams): Promise<Task[]> {
    const response = await organizationTaskClient.listOrganizationTasks({
        query: { ...query, organizationId },
    });

    return response.body.tasks.map((task) => TaskSchema.parse(task));
}

/** Unread thread message counts of the tasks `fetchOrganizationTasks` returns for `query`. */
export async function fetchOrganizationTaskThreadUnreadCounts({
    organizationId,
    query,
}: FetchOrganizationTasksParams): Promise<TaskThreadUnreadCount[]> {
    const response =
        await organizationTaskClient.listOrganizationTaskThreadUnreadCounts({
            query: { ...query, organizationId },
        });

    return response.body.counts;
}

/**
 * Tasks assigned to the current user in To do across every client workspace
 * of the active organization they can open.
 */
export function useOrganizationTodoTasksCount({
    enabled = true,
}: { enabled?: boolean } = {}) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey: organizationTaskQueryKeys.todoCount(organizationId ?? ""),
        queryFn: async () => {
            if (!organizationId) {
                return 0;
            }

            const response =
                await organizationTaskClient.getOrganizationTodoTasksCount({
                    query: { organizationId },
                });

            return response.body.count;
        },
        enabled: enabled && !!organizationId,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
    });
}

const toTasksByWorkspaceId = ({ clients }: ClientTaskPreviewsResponse) =>
    new Map<string, ClientTaskPreview[]>(
        clients.map(({ workspaceId, tasks }) => [workspaceId, tasks]),
    );

/**
 * Up to 5 open tasks per client of the active organization, keyed by
 * workspace id. Clients outside the user's scope are missing from the map.
 */
export function useClientTaskPreviews({
    workspaceIds,
    enabled = true,
}: {
    workspaceIds: readonly string[];
    enabled?: boolean;
}) {
    const { organizationId } = useScope();
    const sortedWorkspaceIds = [...workspaceIds].sort();

    return useQuery({
        queryKey: organizationTaskQueryKeys.clientTaskPreviews({
            organizationId: organizationId ?? "",
            workspaceIds: sortedWorkspaceIds,
        }),
        queryFn: async () => {
            if (!organizationId) {
                return { clients: [] };
            }

            const response =
                await organizationTaskClient.listClientTaskPreviews({
                    query: { organizationId, workspaceIds: sortedWorkspaceIds },
                });

            return response.body;
        },
        select: toTasksByWorkspaceId,
        enabled: enabled && !!organizationId && workspaceIds.length > 0,
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
    });
}
